import { useEffect, useRef } from "react";
import { RotateCcw } from "lucide-react";
import { useAdminPlanView } from "@/hooks/useAdminPlanView";
import { setPlanPreview, usePlanPreview, type PlanKey } from "@/hooks/usePlanUi";
import { setAdminGuestPreview, useAdminGuestPreview } from "@/hooks/useAdminGuestPreview";
import { useLanguage } from "@/i18n/LanguageContext";

/**
 * Solo administrador: pestañas para ver el panel como Admin (acceso total),
 * como visitante sin cuenta o como cada plan. Es solo una vista previa en este
 * navegador: no cobra ni cambia nada en Stripe ni en la cuenta.
 */
type SwitchKey = PlanKey | "admin" | "guest";
const OPTIONS: { key: SwitchKey; es: string; en: string }[] = [
  { key: "admin", es: "Admin", en: "Admin" },
  { key: "guest", es: "Visitante", en: "Visitor" },
  { key: "lite", es: "Lite", en: "Lite" },
  { key: "basico", es: "Básico", en: "Basic" },
  { key: "club_pro", es: "Club Pro", en: "Club Pro" },
  { key: "completo", es: "Avanzado", en: "Advanced" },
];

export default function AdminPlanSwitcher() {
  const { lang } = useLanguage();
  const es = lang === "es";
  const preview = usePlanPreview();
  const guest = useAdminGuestPreview(true);
  const { setView } = useAdminPlanView(true);
  const current: SwitchKey = guest ? "guest" : preview ?? "admin";
  const railRef = useRef<HTMLDivElement>(null);
  // Móvil: la pastilla activa siempre queda a la vista dentro del scroll horizontal.
  useEffect(() => {
    const rail = railRef.current;
    const el = rail?.querySelector<HTMLElement>("[aria-pressed='true']");
    if (rail && el && rail.scrollWidth > rail.clientWidth) rail.scrollLeft = el.offsetLeft - (rail.clientWidth - el.offsetWidth) / 2;
  }, [current]);

  const select = (key: SwitchKey) => {
    if (key === "admin") {
      setAdminGuestPreview(false);
      setPlanPreview(null);
      setView("pro");
    } else if (key === "guest") {
      setPlanPreview(null);
      setView("lite");
      setAdminGuestPreview(true);
      if (window.location.pathname.includes("dashboard")) window.location.replace(es ? "/" : "/en");
    } else {
      setAdminGuestPreview(false);
      setPlanPreview(key);
      // Desactiva los atajos de administrador para ver la experiencia real del plan.
      setView("lite");
    }
  };

  // Reinicia la consulta gratuita de visitante en este navegador.
  const resetGuest = async () => {
    try {
      ["rl-guest-consultations", "rl-pending-question", "rl-guest-first-name", "rl-guest-store-name", "rl-guest-sector"].forEach((k) => window.localStorage.removeItem(k));
    } catch { /* sin almacenamiento */ }
    // También libera la consulta en el servidor para este navegador, así sigue
    // libre aunque después se cierre sesión y se entre como visitante real.
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      await supabase.functions.invoke("support-auto-reply", { body: { guest_mode: true, resetTrial: true } });
    } catch { /* sin conexión: queda el reinicio local */ }
    window.location.replace(es ? "/" : "/en");
  };

  return (
    <div className="inline-flex shrink-0 items-center gap-1">
      {(
        <button
          type="button"
          onClick={resetGuest}
          aria-label={es ? "Reiniciar consulta gratuita" : "Reset free question"}
          title={es ? "Reiniciar consulta gratuita" : "Reset free question"}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-card !text-foreground shadow-sm hover:bg-secondary"
        >
          <RotateCcw className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
        </button>
      )}
      <div
        ref={railRef}
        role="group"
        aria-label={es ? "Ver el panel como" : "View the panel as"}
        className="inline-flex shrink-0 items-center rounded-full border border-border bg-card p-0.5 text-foreground shadow-sm"
      >
        {OPTIONS.map((o) => (
          <button
            key={o.key}
            type="button"
            onClick={() => select(o.key)}
            aria-pressed={current === o.key}
            className={`h-8 shrink-0 whitespace-nowrap rounded-full px-2.5 text-xs font-bold transition-colors ${
              current === o.key ? "bg-foreground !text-background" : "!text-muted-foreground hover:!text-foreground"
            }`}
          >
            {es ? o.es : o.en}
          </button>
        ))}
      </div>
    </div>
  );
}
